<template>
  <div>
    <h2
      class="mt-0 tw-text-2xl tw-font-semibold tw-mb-4"
      style="color: var(--text-color)"
    >
      Snippet Dashboard
    </h2>
    <PanelSkeleton v-if="revenue.isLoading('pace')" shape="card" />

    <div v-else class="tw-flex tw-flex-wrap tw-gap-2">
      <div
        v-for="card in todayCards"
        :key="card.title"
        class="tw-flex-1 tw-basis-56 tw-min-w-0"
      >
        <MetricCard
          :title="card.title"
          :content-str="card.contentStr"
          :card-type="card.cardType"
          :sub-content-str="card.subContentStr"
          :loader="card.loader"
          :is-plus="card.isPlus"
          :info="card.info"
        />
      </div>
    </div>

    <PanelSkeleton v-if="revenue.isLoading('hourly')" class="my-3" />

    <Card v-else class="my-3">
      <template #title>
        <div class="text-lg font-semibold">Revenue — Today vs Yesterday</div>
      </template>
      <template #content>
        <line-chart v-if="hasHourlyData" :data="hourlyChart" prefix="$" />
        <div v-else class="chart-empty text-sm text-bluegray-500">
          {{ SNIPPET_EMPTY_MESSAGE }}
        </div>
      </template>
    </Card>

    <div
      class="grid align-items-end mt-4 mb-2"
      style="color: var(--text-color)"
    >
      <div class="col-12 sm:col-6 lg:col-3 flex flex-column gap-1">
        <label for="snippet-range">Select Date Range</label>
        <Calendar
          input-id="snippet-range"
          :model-value="revenue.dateRange"
          :show-icon="true"
          :number-of-months="2"
          selection-mode="range"
          class="w-full"
          :disabled="isBusy"
          @update:model-value="setDateRange"
        />
      </div>
      <div
        v-for="picker in memberPickers"
        :key="picker.key"
        class="col-12 sm:col-6 lg:col-3 flex flex-column gap-1"
      >
        <label :for="`snippet-${picker.key}`">{{ picker.label }}</label>
        <MultiSelect
          :input-id="`snippet-${picker.key}`"
          filter
          display="chip"
          :options="picker.options"
          :model-value="revenue.memberFilter[picker.key]"
          option-label="name"
          option-value="id"
          :placeholder="picker.label"
          class="w-full"
          :loading="revenue.isLoading('placements')"
          :disabled="isBusy"
          @change="(e) => setMemberFilter({ [picker.key]: e.value })"
        />
      </div>
      <div class="col-12 sm:col-6 lg:col-3">
        <Button
          class="justify-content-center w-full"
          :disabled="isBusy"
          :loading="isBusy"
          @click="reloadActiveTab"
          >Fetch Data</Button
        >
      </div>
    </div>

    <TabView
      :active-index="activeTab"
      @tab-change="(e) => setTab(TABS[e.index])"
    >
      <TabPanel header="Revenue">
        <SnippetRevenue />
      </TabPanel>
      <TabPanel header="Engagement">
        <SnippetEngagement />
      </TabPanel>
    </TabView>
  </div>
</template>

<script setup>
import { computed, onBeforeMount, ref, watch } from "vue";
import Button from "primevue/button";
import Calendar from "primevue/calendar";
import MultiSelect from "primevue/multiselect";
import Card from "primevue/card";
import TabView from "primevue/tabview";
import TabPanel from "primevue/tabpanel";
import PanelSkeleton from "./PanelSkeleton.vue";
import MetricCard from "./MetricCard.vue";
import SnippetRevenue from "@/components/SnippetRevenue/SnippetRevenue.vue";
import SnippetEngagement from "@/components/SnippetEngagement/SnippetEngagement.vue";
import { formatMoney } from "@/helpers";
import { SNIPPET_EMPTY_MESSAGE } from "@/consts";
import { useSnippetRevenueStore } from "@/stores/snippetRevenue";
import { snippetViewerRole } from "@/composables/useSnippetApiToken";
import { useSnippetEngagementStore } from "@/stores/snippetEngagement";
import { useTabState } from "@/composables/useTabState";
import { useRoute } from "vue-router";

const revenue = useSnippetRevenueStore();
const engagement = useSnippetEngagementStore();

const TABS = ["revenue", "engagement"];
const { tab, setTab } = useTabState(TABS, "revenue");
const activeTab = computed(() => TABS.indexOf(tab.value));

const loadedTabs = ref(new Set());

const count = (value) => formatMoney(value, "decimal");
const money = (value) => formatMoney(value);

const percent = (value) =>
  value === null ? "—" : `${(value * 100).toFixed(2)}%`;

const ELAPSED_NOTE = "Change vs the same hours yesterday.";

const TODAY_INFO = {
  revenue: ELAPSED_NOTE,
  impressions: ELAPSED_NOTE,
  loads: `Content requests, one per widget on the page. Shared with the older widget on the same placement. ${ELAPSED_NOTE}`,
};

const paceCard = (title, metric, format) => {
  const change = revenue.pace.change[metric];

  return {
    title,
    contentStr: format(revenue.pace.today[metric]),
    cardType: change === null ? "normal" : "diff",
    subContentStr: percent(change),
    loader: revenue.isLoading("pace") ? 1 : 0,
    isPlus: change !== null && change >= 0,
    info: TODAY_INFO[metric],
  };
};

const todayCards = computed(() => [
  paceCard("Revenue Today", "revenue", money),
  paceCard("Impressions Today", "impressions", count),
  paceCard("Loads Today", "loads", count),
  {
    title: "Monthly Run Rate",
    contentStr: money(revenue.runRate.mrr),
    cardType: "normal",
    subContentStr: `Average Daily: ${money(
      revenue.runRate.averageDailyRevenue
    )}`,
    loader: revenue.isLoading("runRate") ? 1 : 0,
  },
]);

const toSeries = (points) =>
  Object.fromEntries(
    points.map(({ hour, revenue: value }) => [
      `${String(hour).padStart(2, "0")}:00`,
      value,
    ])
  );

const hourlyChart = computed(() => [
  { name: "Today", data: toSeries(revenue.hourly.today) },
  { name: "Yesterday", data: toSeries(revenue.hourly.yesterday) },
]);

const hasHourlyData = computed(() =>
  [...revenue.hourly.today, ...revenue.hourly.yesterday].some(
    (point) => point.revenue > 0
  )
);

const distinctBy = (rows, id, name) => {
  const seen = new Map();

  rows.forEach((row) => {
    const key = row[id];
    if (key === null || key === undefined || key === "") return;
    if (!seen.has(key))
      seen.set(key, { id: String(key), name: row[name] || String(key) });
  });

  return [...seen.values()].sort((a, b) => a.name.localeCompare(b.name));
};

const PICKERS_BY_ROLE = {
  admin: ["partners", "groups", "sellers"],
  "group-admin": ["sellers"],
  seller: [],
};

const PICKERS = {
  partners: {
    label: "Select Partners",
    column: "seller",
    name: "partner_name",
  },
  groups: {
    label: "Select Groups",
    column: "seller_group_id",
    name: "group_name",
  },
  sellers: {
    label: "Select Sellers",
    column: "sub_seller",
    name: "seller_name",
  },
};

const memberPickers = computed(() =>
  (PICKERS_BY_ROLE[snippetViewerRole()] ?? []).map((key) => ({
    key,
    label: PICKERS[key].label,
    options: distinctBy(
      revenue.placements,
      PICKERS[key].column,
      PICKERS[key].name
    ),
  }))
);

const setMemberFilter = (value) => {
  revenue.setMemberFilter(value);
  engagement.setMemberFilter(value);
  loadedTabs.value = new Set();
};

const setDateRange = (value) => {
  revenue.setDateRange(value);
  engagement.setDateRange(value);
  loadedTabs.value = new Set();
};

const loadTab = (index) => {
  if (index === 0) revenue.loadRange();
  else engagement.loadRange();
  loadedTabs.value = new Set(loadedTabs.value).add(index);
};

const reloadActiveTab = () => loadTab(activeTab.value);

const isBusy = computed(() => revenue.isBusy || engagement.isBusy);

watch(activeTab, (index) => {
  if (!loadedTabs.value.has(index)) loadTab(index);
});

const route = useRoute();

onBeforeMount(() => {
  const asSellerId = route.query.asSellerId ?? null;
  revenue.setViewAsSellerId(asSellerId);
  engagement.setViewAsSellerId(asSellerId);

  revenue.loadPlacements();
  revenue.loadToday();
  loadTab(activeTab.value);
});
</script>

<style scoped>
.chart-empty {
  display: flex;
  align-items: center;
  min-height: 300px;
}
</style>
